// src/components/ExternalPayment.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

const ExternalPayment = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('redirect');

  // Your Razorpay payment page URL
  const RAZORPAY_PAYMENT_URL = 'https://rzp.io/rzp/LkQzfpF';

  useEffect(() => {
    loadPendingOrders();
  }, []);

  const loadPendingOrders = async () => {
    try {
      setLoading(true);
      // Get pending orders from your existing API
      const allOrders = await apiService.getAllOrders();
      // Filter for pending payment orders
      const pendingOrders = allOrders.filter(order => 
        order.status === 'PENDING' || 
        order.status === 'PAYMENT_PENDING' || 
        order.status === 'PROCESSING'
      );
      setOrders(pendingOrders);
    } catch (err) {
      setError('Failed to load orders: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Method 1: Direct redirect to payment page
  const handleDirectPayment = (order) => {
    const paymentUrl = buildPaymentUrl(order);
    window.location.href = paymentUrl;
  };

  // Method 2: Open in new tab
  const handlePaymentNewTab = (order) => {
    const paymentUrl = buildPaymentUrl(order);
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  };

  // Method 3: Embedded iframe (optional)
  const handleEmbeddedPayment = (order) => {
    setSelectedOrder(order);
  };

  // Build payment URL with order parameters
  const buildPaymentUrl = (order) => {
    const baseUrl = RAZORPAY_PAYMENT_URL;
    const params = new URLSearchParams({
      // Add order details as query parameters if your payment page supports them
      amount: (order.totalAmount * 100).toString(), // Amount in paise
      order_id: order.id,
      customer_name: order.customerName || 'Customer',
      customer_email: order.customerEmail || '',
      customer_phone: order.customerPhone || '',
      description: `Payment for Order #${order.id}`,
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  // Calculate order total
  const getOrderTotal = (order) => {
    if (order.totalAmount) return order.totalAmount;
    if (order.orderItems) {
      return order.orderItems.reduce((total, item) => total + (item.quantity * item.price), 0);
    }
    return order.price * order.quantity || 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  if (loading) {
    return <div className="loading-container">Loading orders...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={loadPendingOrders} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="external-payment-container">
      <div className="page-header">
        <h2>External Payment Processing</h2>
        <p className="page-description">
          Process payments for pending orders using Razorpay Payment Page
        </p>
      </div>

      {/* Payment Method Selection */}
      <div className="payment-method-selector">
        <h3>Payment Method</h3>
        <div className="method-options">
          <label className="method-option">
            <input
              type="radio"
              value="redirect"
              checked={paymentMethod === 'redirect'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <span>Redirect to Payment Page</span>
            <small>Navigate to Razorpay payment page</small>
          </label>
          <label className="method-option">
            <input
              type="radio"
              value="newTab"
              checked={paymentMethod === 'newTab'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <span>Open in New Tab</span>
            <small>Keep dashboard open while processing payment</small>
          </label>
          <label className="method-option">
            <input
              type="radio"
              value="embedded"
              checked={paymentMethod === 'embedded'}
              onChange={(e) => setPaymentMethod(e.target.value)}
            />
            <span>Embedded Payment</span>
            <small>Show payment page within dashboard</small>
          </label>
        </div>
      </div>

      {/* Orders List */}
      <div className="orders-section">
        <div className="section-header">
          <h3>Pending Payment Orders ({orders.length})</h3>
          <button onClick={loadPendingOrders} className="refresh-btn">
            🔄 Refresh
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="no-orders">
            <p>No pending payment orders found.</p>
          </div>
        ) : (
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Created Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id}>
                    <td>
                      <span className="order-id">#{order.id}</span>
                    </td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">
                          {order.customerName || `Customer ${order.customerId}`}
                        </div>
                        {order.customerEmail && (
                          <div className="customer-email">{order.customerEmail}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="amount">{formatCurrency(getOrderTotal(order))}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${order.status?.toLowerCase()}`}>
                        {order.status || 'PENDING'}
                      </span>
                    </td>
                    <td>{formatDate(order.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        {paymentMethod === 'redirect' && (
                          <button
                            onClick={() => handleDirectPayment(order)}
                            className="pay-btn primary"
                          >
                            💳 Pay Now
                          </button>
                        )}
                        {paymentMethod === 'newTab' && (
                          <button
                            onClick={() => handlePaymentNewTab(order)}
                            className="pay-btn secondary"
                          >
                            🔗 Pay in New Tab
                          </button>
                        )}
                        {paymentMethod === 'embedded' && (
                          <button
                            onClick={() => handleEmbeddedPayment(order)}
                            className="pay-btn tertiary"
                          >
                            📱 Embedded Pay
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Embedded Payment Modal */}
      {selectedOrder && paymentMethod === 'embedded' && (
        <div className="payment-modal-overlay">
          <div className="payment-modal">
            <div className="modal-header">
              <h3>Payment for Order #{selectedOrder.id}</h3>
              <button
                onClick={() => setSelectedOrder(null)}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="order-summary">
                <p><strong>Customer:</strong> {selectedOrder.customerName}</p>
                <p><strong>Amount:</strong> {formatCurrency(getOrderTotal(selectedOrder))}</p>
              </div>
              <div className="iframe-container">
                <iframe
                  src={buildPaymentUrl(selectedOrder)}
                  className="payment-iframe"
                  title={`Payment for Order ${selectedOrder.id}`}
                  frameBorder="0"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => handlePaymentNewTab(selectedOrder)}
                className="open-external-btn"
              >
                Open in New Tab
              </button>
              <button
                onClick={() => setSelectedOrder(null)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Payment Link Section */}
      <div className="quick-payment-section">
        <h3>Quick Payment Link</h3>
        <div className="payment-link-container">
          <input
            type="text"
            value={RAZORPAY_PAYMENT_URL}
            readOnly
            className="payment-link-input"
          />
          <button
            onClick={() => navigator.clipboard.writeText(RAZORPAY_PAYMENT_URL)}
            className="copy-btn"
          >
            📋 Copy Link
          </button>
          <button
            onClick={() => window.open(RAZORPAY_PAYMENT_URL, '_blank')}
            className="open-btn"
          >
            🔗 Open Link
          </button>
        </div>
        <p className="link-description">
          Share this link directly with customers for manual payments
        </p>
      </div>

      <style jsx>{`
        .external-payment-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          margin-bottom: 30px;
        }

        .page-header h2 {
          color: #2c3e50;
          margin-bottom: 10px;
        }

        .page-description {
          color: #666;
          font-size: 14px;
        }

        .payment-method-selector {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
        }

        .payment-method-selector h3 {
          margin-bottom: 15px;
          color: #2c3e50;
        }

        .method-options {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .method-option {
          display: flex;
          flex-direction: column;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          cursor: pointer;
          background: white;
          transition: all 0.2s ease;
        }

        .method-option:hover {
          border-color: #007bff;
          background: #f0f8ff;
        }

        .method-option input[type="radio"] {
          margin-right: 8px;
        }

        .method-option small {
          color: #666;
          margin-left: 20px;
          font-size: 12px;
        }

        .orders-section {
          margin-bottom: 30px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .section-header h3 {
          color: #2c3e50;
          margin: 0;
        }

        .refresh-btn {
          background: #6c757d;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .refresh-btn:hover {
          background: #5a6268;
        }

        .orders-table-container {
          overflow-x: auto;
          border: 1px solid #ddd;
          border-radius: 8px;
        }

        .orders-table {
          width: 100%;
          border-collapse: collapse;
        }

        .orders-table th,
        .orders-table td {
          padding: 12px;
          text-align: left;
          border-bottom: 1px solid #eee;
        }

        .orders-table th {
          background: #f8f9fa;
          font-weight: 600;
          color: #495057;
        }

        .orders-table tr:hover {
          background: #f8f9fa;
        }

        .order-id {
          font-family: 'Courier New', monospace;
          font-weight: 600;
          color: #495057;
        }

        .customer-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .customer-name {
          font-weight: 500;
        }

        .customer-email {
          font-size: 12px;
          color: #666;
        }

        .amount {
          font-weight: 600;
          color: #28a745;
          font-size: 16px;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .status-badge.pending {
          background: #fff3cd;
          color: #856404;
        }

        .status-badge.processing {
          background: #cce5ff;
          color: #0066cc;
        }

        .status-badge.payment_pending {
          background: #f8d7da;
          color: #721c24;
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .pay-btn {
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }

        .pay-btn.primary {
          background: #007bff;
          color: white;
        }

        .pay-btn.primary:hover {
          background: #0056b3;
        }

        .pay-btn.secondary {
          background: #6c757d;
          color: white;
        }

        .pay-btn.secondary:hover {
          background: #5a6268;
        }

        .pay-btn.tertiary {
          background: #17a2b8;
          color: white;
        }

        .pay-btn.tertiary:hover {
          background: #138496;
        }

        .payment-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .payment-modal {
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 800px;
          height: 90%;
          max-height: 600px;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          color: #2c3e50;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 20px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
        }

        .close-btn:hover {
          background: #f0f0f0;
        }

        .modal-body {
          flex: 1;
          padding: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .order-summary {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .iframe-container {
          flex: 1;
          border: 1px solid #ddd;
          border-radius: 6px;
          overflow: hidden;
        }

        .payment-iframe {
          width: 100%;
          height: 100%;
          border: none;
        }

        .modal-footer {
          padding: 20px;
          border-top: 1px solid #eee;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .open-external-btn {
          background: #17a2b8;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }

        .cancel-btn {
          background: #6c757d;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }

        .quick-payment-section {
          background: #e9ecef;
          padding: 20px;
          border-radius: 8px;
        }

        .quick-payment-section h3 {
          color: #2c3e50;
          margin-bottom: 15px;
        }

        .payment-link-container {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
        }

        .payment-link-input {
          flex: 1;
          padding: 10px;
          border: 1px solid #ccc;
          border-radius: 4px;
          background: white;
        }

        .copy-btn,
        .open-btn {
          background: #28a745;
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .copy-btn:hover,
        .open-btn:hover {
          background: #218838;
        }

        .link-description {
          color: #666;
          font-size: 14px;
          margin: 0;
        }

        .no-orders {
          text-align: center;
          padding: 40px;
          color: #666;
        }

        .loading-container,
        .error-container {
          text-align: center;
          padding: 40px;
        }

        .retry-btn {
          background: #007bff;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          margin-top: 15px;
        }

        @media (max-width: 768px) {
          .external-payment-container {
            padding: 10px;
          }

          .method-options {
            gap: 8px;
          }

          .payment-link-container {
            flex-direction: column;
          }

          .orders-table {
            font-size: 14px;
          }

          .payment-modal {
            width: 95%;
            height: 95%;
          }
        }
      `}</style>
    </div>
  );
};

export default ExternalPayment;